import React, { useState, useEffect } from 'react';
import './LandingPage.css';

const LandingPage = () => {
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token');
    
    if (urlToken) {
      setToken(urlToken);
      // Store token immediately when received via URL
      localStorage.setItem('pendingInvitationToken', urlToken);
      console.log('Stored invitation token from URL:', urlToken);
    } else {
      // Check if there's a previously stored token (user returning after app installation)
      const storedToken = localStorage.getItem('pendingInvitationToken');
      if (storedToken) {
        setToken(storedToken);
        console.log('Found previously stored invitation token:', storedToken);
      }
    }
  }, []);

  const attemptToOpenApp = () => {
    setLoading(true);
    
    const deepLinkUrl = token ? `myapp://invite?token=${token}` : 'myapp://';
    
    // Create a hidden iframe to attempt opening the app
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = deepLinkUrl;
    document.body.appendChild(iframe);
    
    // Also try direct navigation
    window.location.href = deepLinkUrl;
    
    // Set timeout to show app store if app doesn't open
    setTimeout(() => {
      setLoading(false);
      showAppStorePrompt();
    }, 2500);
    
    // Clear token from localStorage if app opens successfully
    // (This won't execute if the app takes over, which is what we want)
    setTimeout(() => {
      if (token) {
        localStorage.removeItem('pendingInvitationToken');
        console.log('Cleared invitation token - app likely opened successfully');
      }
    }, 1000);
  };

  const showAppStorePrompt = () => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);
    
    const APP_STORE_URL = 'https://apps.apple.com/us/app/the-nextlink/id6743041580';
    const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.nextgenexpresstech.nextlink';
    
    // Store invitation token in localStorage for when user returns with the app installed
    if (token) {
      localStorage.setItem('pendingInvitationToken', token);
      console.log('Stored invitation token for after app installation:', token);
    }
    
    if (isIOS) {
      window.location.href = APP_STORE_URL;
    } else if (isAndroid) {
      window.location.href = PLAY_STORE_URL;
    } else {
      alert('NextLink is available on mobile devices. Please visit the App Store or Google Play to download.');
    }
  };

  return (
    <div className="landing-container">
      {/* Navigation Header */}
      <nav className="navbar">
        <div className="nav-container">
          <div className="nav-brand">
            <img src="/logo_white.png" alt="NextLink" className="brand-logo-img" />
            <span className="brand-name">NextLink</span>
          </div>
          <div className="nav-menu">
            <a href="#features" className="nav-link">Features</a>
            <a href="#tools" className="nav-link">Tools</a>
            <a href="#use-cases" className="nav-link">Use Cases</a>
            <button className="nav-cta-btn" onClick={attemptToOpenApp}>
              {token ? 'Join Group' : 'Download'}
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-container">
          {token && (
            <div className="invitation-alert">
              <span className="alert-icon">📧</span>
              <span>You've been invited to join a group on NextLink</span>
            </div>
          )}
          
          <div className="hero-content">
            <h1 className="hero-title">
              Connect your community with
              <span className="title-accent"> NextLink</span>
            </h1>
            <p className="hero-subtitle">
              The complete platform for group communication, organization, and collaboration. 
              Built for communities that need more than basic messaging.
            </p>
            
            <div className="hero-actions">
              {!loading ? (
                <>
                  <button className="btn-primary" onClick={attemptToOpenApp}>
                    {token ? 'Accept Invitation' : 'Get Started'}
                  </button>
                  <button className="btn-secondary" onClick={() => document.getElementById('features').scrollIntoView()}>
                    Learn More
                  </button>
                </>
              ) : (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <span>Opening NextLink...</span>
                </div>
              )}
            </div>
            
            <div className="download-badges">
              <a href="https://apps.apple.com/us/app/the-nextlink/id6743041580" target="_blank" rel="noopener noreferrer" className='download-badge'>
                <img 
                  src='/app-store-badge.png'
                  alt='Download on the App Store'
                  width='150px'
                  height='50px'
                />
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.nextgenexpresstech.nextlink" target="_blank" rel="noopener noreferrer" className='download-badge'>
                <img 
                  src='/google-play-badge.png'
                  alt='Get it on Google Play'
                  width='170px'
                  height='75px'
                />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Core Features */}
      <section id="features" className="features-section">
        <div className="container">
          <div className="section-header">
            <h2>Everything you need for community management</h2>
            <p className="section-subtitle">NextLink combines powerful messaging with specialized tools for organization and collaboration.</p>
          </div>
          
          <div className="features-grid">
            <div className="feature-item">
              <div className="feature-content">
                <div className="feature-icon">💬</div>
                <h3>Rich Group Chat</h3>
                <p>Real-time messaging with file sharing, image support, URL previews, and message reactions. Keep conversations organized and engaging.</p>
              </div>
            </div>
            
            <div className="feature-item">
              <div className="feature-content">
                <div className="feature-icon">👥</div>
                <h3>Flexible Groups</h3>
                <p>Create public or private groups, send invitations, and organize members into subgroups for better management and coordination.</p>
              </div>
            </div>
            
            <div className="feature-item">
              <div className="feature-content">
                <div className="feature-icon">📱</div>
                <h3>Mobile-First Design</h3>
                <p>Native mobile apps for iOS and Android with seamless synchronization and offline support for uninterrupted communication.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Advanced Tools */}
      <section id="tools" className="tools-section">
        <div className="container">
          <div className="section-header">
            <h2>Advanced Community Tools</h2>
            <p className="section-subtitle">Go beyond basic messaging with specialized tools designed for community engagement and organization.</p>
          </div>
          
          <div className="tools-grid">
            <div className="tool-card">
              <div className="tool-header">
                <div className="tool-icon">📊</div>
                <h3>Polls & Voting</h3>
              </div>
              <p>Make group decisions democratically with built-in polling. Create custom polls, track responses, and reach consensus quickly.</p>
            </div>
            
            <div className="tool-card">
              <div className="tool-header">
                <div className="tool-icon">📋</div>
                <h3>Forms & Data Collection</h3>
              </div>
              <p>Collect structured information from group members. Perfect for gathering shirt sizes, dietary preferences, or event details.</p>
            </div>
            
            <div className="tool-card">
              <div className="tool-header">
                <div className="tool-icon">🔗</div>
                <h3>Shared Resource Links</h3>
              </div>
              <p>Organize important URLs in one accessible place. Great for conference hotels, group discounts, and shared resources.</p>
            </div>
            
            <div className="tool-card">
              <div className="tool-header">
                <div className="tool-icon">✋</div>
                <h3>Event Sign-ups</h3>
              </div>
              <p>Coordinate activities with easy sign-up sheets. Manage shifts, volunteer opportunities, and event participation.</p>
            </div>
            
            <div className="tool-card featured">
              <div className="tool-header">
                <div className="tool-icon">💳</div>
                <h3>Group Payments</h3>
                <div className="feature-badge">Stripe Integration</div>
              </div>
              <p>Handle group finances seamlessly. Request and process payments securely with integrated Stripe support.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section id="use-cases" className="use-cases-section">
        <div className="container">
          <div className="section-header">
            <h2>Built for every type of community</h2>
          </div>
          
          <div className="use-cases-grid">
            <div className="use-case">
              <div className="use-case-icon">🎓</div>
              <h3>Student Organizations</h3>
              <p>Coordinate events, collect dues, share resources, and keep members engaged throughout the academic year.</p>
            </div>
            
            <div className="use-case">
              <div className="use-case-icon">🏢</div>
              <h3>Professional Teams</h3>
              <p>Enhance workplace collaboration with organized communication, resource sharing, and project coordination.</p>
            </div>
            
            <div className="use-case">
              <div className="use-case-icon">🎉</div>
              <h3>Event Planning</h3>
              <p>Organize gatherings, coordinate logistics, and manage group payments for seamless events.</p>
            </div>
            
            <div className="use-case">
              <div className="use-case-icon">⚽</div>
              <h3>Sports & Recreation</h3>
              <p>Manage team communications, schedule games, coordinate carpools, and handle team expenses effortlessly.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to connect your community?</h2>
            <p>Join thousands of groups using NextLink to stay connected, organized, and engaged.</p>
            <div className="cta-actions">
              <button className="btn-primary large" onClick={attemptToOpenApp}>
                {token ? 'Join Your Group' : 'Get Started Today'}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="footer-logo">
                <img src="/logo_white.png" alt="NextLink" className="brand-logo-img" />
                <span className="brand-name">NextLink</span>
              </div>
              <p>The complete community platform for modern groups.</p>
            </div>
            
            <div className="footer-links">
              <div className="footer-column">
                <h4>Product</h4>
                <a href="#features">Features</a>
                <a href="#tools">Tools</a>
                <a href="#use-cases">Use Cases</a>
              </div>
              
              <div className="footer-column">
                <h4>Download</h4>
                <a href="https://apps.apple.com/us/app/the-nextlink/id6743041580" target="_blank" rel="noopener noreferrer">iOS App</a>
                <a href="https://play.google.com/store/apps/details?id=com.nextgenexpresstech.nextlink" target="_blank" rel="noopener noreferrer">Android App</a>
              </div>
              
              <div className="footer-column">
                <h4>Contact</h4>
                <a href="mailto:support@nextlinkchat.com">support@nextlinkchat.com</a>
              </div>
            </div>
          </div>
          
          <div className="footer-bottom">
            <p>&copy; {new Date().getFullYear()} NextLink, LLC. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;